import { parseISO } from 'date-fns';
import { z } from 'zod';
import axiosRetry from 'axios-retry';

import config from '@lib/config';
import { DeliveryCheckPayload, StreetAddress } from '@lib/entities/Address';
import {
    $pickupTimesPayload,
    $timeSlotPayload,
    type RemoteStore,
    type Store,
    compareDistance,
    hasValidLocation,
    remoteToLocal
} from '@lib/entities/Store';
import { Either } from '@lib/monad';
import { camelKeys } from '@lib/utils/casing';
import { showError } from '@lib/utils/toast';

import client, { isApiError, wrapApiCall } from './client';
import { type FetchResponse, declareFetch } from './declarativeClient';
import { isMalformedResponseError } from './errors';

const fetchStores = wrapApiCall(
    async (lat?: number, lng?: number, pageSize = 100): Promise<RemoteStore[]> => {
        // Configure retry for this specific request
        const requestConfig = {
            params: {
                lat,
                lng,
                pageSize
            }
        };

        // Apply retry configuration to the client for this request
        axiosRetry(client, {
            retries: 3,
            retryCondition: axiosRetry.isNetworkOrIdempotentRequestError
        });

        const { data } = await client.get('v3/storeLocations/stores', requestConfig);

        return data.data;
    }
);

const fetchStoresAsLocal = async (lat: number, lng: number): Promise<Store[]> => {
    const remoteStores = await fetchStores(lat, lng);

    if (isApiError(remoteStores)) {
        showError(remoteStores.error);
        return [];
    }

    return remoteStores.filter(hasValidLocation).map(remoteToLocal(lat, lng)).sort(compareDistance);
};

interface TimeSlotParams {
    storeId: number;
    cartItemCount?: number;
}
type TimeSlotPayloadSchema = typeof $timeSlotPayload;
type TimeSlotPayload = z.infer<TimeSlotPayloadSchema>;
const fetchDeliveryTimesRaw = declareFetch<TimeSlotPayloadSchema, TimeSlotParams>({
    path: 'v1/StoreLocations/delivery-times',
    schema: $timeSlotPayload
});

const handleTimeSlotResponse = (response: FetchResponse<TimeSlotPayload>) => {
    const slots = Either.bind(response)
        .lift((times) => {
            return times.map((time) => parseISO(time));
        })
        .catch((error) => {
            if (isMalformedResponseError(error) && config.env === 'production') {
                if (Array.isArray(error.recovery)) {
                    return Either.right(error.recovery as Date[]);
                }
                return Either.right([]);
            }
            return Either.left(error);
        }).return;

    if (Either.isLeft(slots)) {
        throw Either.unwrap(slots);
    }
    return Either.unwrap(slots);
};

const fetchDeliveryTimes = async (storeId: number) => {
    const result = await fetchDeliveryTimesRaw({ storeId });
    return handleTimeSlotResponse(result);
};

const fetchPickupTimesRaw = declareFetch<typeof $pickupTimesPayload, TimeSlotParams>({
    path: 'v2/StoreLocations/pickup-times',
    schema: $pickupTimesPayload
});

const fetchPickupTimes = async (storeId: number, cartItemCount?: number) => {
    const raw = await fetchPickupTimesRaw({ storeId, cartItemCount });
    const result = Either.isRight(raw) ? Either.right(raw.right.times) : raw;
    return handleTimeSlotResponse(result);
};

export type AddressQuery = Pick<
    StreetAddress,
    'address_1' | 'address_2' | 'zip' | 'city' | 'state'
>;

const checkDeliveryAddress = wrapApiCall(
    async ({
        address_1,
        address_2,
        city,
        state,
        zip
    }: AddressQuery): Promise<DeliveryCheckPayload> => {
        const { data } = await client.post('v2/storelocations/verify-address-info', {
            address_1,
            address_2,
            city,
            state,
            zip
        });

        return camelKeys(data) as unknown as DeliveryCheckPayload;
    }
);

export default {
    fetchStores,
    fetchStoresAsLocal,
    fetchPickupTimes,
    fetchDeliveryTimes,
    checkDeliveryAddress
};
